import {
  keepPreviousData,
  useMutation,
  useQuery,
} from '@mint-lib/query-client';
import { useFlags } from '@mint-lib/routing-context';
import { Auth } from '@mint-sdk/api-auth';

import useConditionalSearch from '../../hooks/useConditionalSearch/useConditionalSearch.js';
import useTab, { RouteTabs } from '../../hooks/useTab/useTab.js';
import useTablePagination from '../../hooks/useTablePagination/useTablePagination.jsx';
import useTableSorting from '../../hooks/useTableSorting/useTableSorting.jsx';
import {
  createRole as createRoleReq,
  createRoleV2,
  deleteRole as deleteRoleReq,
  editRole as editRoleReq,
  editRoleV2,
} from '../roles/index.js';
import { queryClient } from './main.js';

type Params = { searchQuery?: string; getFullList?: boolean };

export const useUserRole = (params?: Params) => {
  const ff = useFlags('@mint/arm', ['aiHubPermission', 'mint2Permissions']);
  const searchParams = useConditionalSearch();

  const { limit, offset } = useTablePagination();

  const { getFullList } = params ?? {};

  const { orderingBE } = useTableSorting();

  const searchString = getFullList ? '' : searchParams?.search || '';

  const tab = useTab();

  const paginatedRoles = useQuery({
    ...Auth.listPaginatedRoles({
      params: {
        search: searchString,
        limit: limit,
        offset,
        ordering: orderingBE,
        ...(ff.aiHubPermission.enabled && { isAiHubIncluded: true }),
      },
    }),

    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled: tab === RouteTabs.Role && !getFullList && limit !== 0,
  });

  const allRoles = useQuery({
    ...Auth.listAllRolesV2({
      params: {
        search: searchString,
        limit: 0,
        offset,
        ordering: orderingBE,
        ...(ff.aiHubPermission.enabled && { isAiHubIncluded: true }),
      },
    }),

    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled: Boolean(getFullList) || limit === 0,
  });

  const createRole = useMutation({
    mutationFn: ff.mint2Permissions.enabled ? createRoleV2 : createRoleReq,
  });

  const editRole = useMutation({
    mutationFn: ff.mint2Permissions.enabled ? editRoleV2 : editRoleReq,
  });

  const deleteRole = useMutation({
    mutationFn: deleteRoleReq,
  });

  const refetchRoles = () => {
    queryClient.invalidateQueries(
      Auth.listPaginatedRoles({
        params: {},
      }),
    );
  };

  return {
    userRole: paginatedRoles,
    allRoles: allRoles,
    createRole,
    editRole,
    deleteRole,
    refetchRoles,
  };
};
